export class ConditionalFormHandling {
  constructor(conditionalFieldMap, formId) {
    this.conditionalFieldMapping = conditionalFieldMap;
    this.formElement = document.querySelector(`hi-form#${formId}`);
    this.initializeFormWithConditionalLogic();
  }
  initializeFormWithConditionalLogic() {
    Object.keys(this.conditionalFieldMapping).forEach((nodeId) => {
      const controllingElement = this.formElement.querySelector(`[data-node-id="${nodeId}"]`);
      if (!controllingElement) return;
      let initialValue;
      switch (controllingElement.tagName) {
        case "HI-COMBOBOX":
          initialValue = controllingElement.multiple ? controllingElement.values : controllingElement.value;
          break;
        default:
          initialValue = controllingElement.value;
      }
      if (initialValue !== void 0) {
        this.lookForMatchingLogic(nodeId, initialValue);
      }
    });
  }
  addFormEventListener() {
    this.formElement.addEventListener("value-changed", (event) => {
      if (!event.detail.originElement) return;
      const controllingFieldValue = event.detail.srcValue;
      const controllingFieldNodeId = event.detail.originElement.dataset.nodeId;
      this.lookForMatchingLogic(controllingFieldNodeId, controllingFieldValue);
    });
  }
  lookForMatchingLogic(controllingFieldNodeId, controllingFieldValue) {
    const matchingLogic = this.conditionalFieldMapping[controllingFieldNodeId] || [];
    if (!matchingLogic.length) return;
    this.handleMatchingLogic(matchingLogic, controllingFieldValue);
  }
  handleMatchingLogic(matchingLogic, triggeredValue) {
    matchingLogic.forEach((logic) => {
      const groupToControl = this.getGroupToControl(logic);
      const valuesMatch = this.compareValues(logic.trigger_value, triggeredValue, logic.trigger_type);
      if (logic.trigger_action === "show") {
        groupToControl.classList.toggle("hide", !valuesMatch);
      } else if (logic.trigger_action === "hide") {
        groupToControl.classList.toggle("hide", valuesMatch);
      }
      if (this.shouldHideElements(logic, valuesMatch)) {
        this.updateValues(groupToControl, logic);
        this.removeRequired(groupToControl);
      } else if (this.shouldShowElements(logic)) {
        this.addRequired(groupToControl, logic);
      }
    });
  }
  getGroupToControl(logic) {
    const elementToControl = document.querySelector(`[data-node-id="${logic.node_id}"]`);
    if (logic.node_id.includes("section")) {
      return elementToControl;
    }
    return elementToControl.closest("hi-input-group");
  }
  shouldHideElements(logic, valuesMatch) {
    if (logic.node_id.includes("section")) return false;
    if (logic.trigger_action === "hide" && valuesMatch) return true;
    if (!valuesMatch) return true;
    return false;
  }
  shouldShowElements(logic) {
    if (logic.node_id.includes("section")) return false;
    return true;
  }
  updateValues(groupToControl, matchingLogic) {
    if (matchingLogic.default_value === null) {
      this.clearValues(groupToControl);
    } else {
      this.updateDefaultValue(groupToControl, matchingLogic);
    }
  }
  clearValues(elementToControl) {
    const elementToChange = elementToControl.querySelector(":not(div):not(hi-label)");
    switch (elementToChange.tagName) {
      case "HI-COMBOBOX": {
        elementToChange.clearInput();
        break;
      }
      default: {
        if (typeof elementToChange.clear === "function") elementToChange.clear();
      }
    }
  }
  updateDefaultValue(elementToControl, matchingLogic) {
    const defaultValue = matchingLogic.default_value;
    const elementToChange = elementToControl.querySelector(":not(div):not(hi-label)");
    switch (elementToChange.tagName) {
      case "HI-COMBOBOX": {
        elementToChange.values = [defaultValue];
        break;
      }
      case "HI-INPUT": {
        if (elementToChange.type === "date") {
          const date = new Date(defaultValue[0]);
          elementToChange.value = date.toLocaleDateString("en-CA");
        } else {
          elementToChange.value = defaultValue[0];
        }
        break;
      }
      default: {
        elementToChange.value = defaultValue[0];
      }
    }
  }
  removeRequired(elementToControl) {
    elementToControl.querySelectorAll(":not(div):not(hi-tooltip)").forEach((el) => el.removeAttribute("required"));
  }
  addRequired(elementToControl, matchingLogic) {
    if (matchingLogic["field_required"] === false) return;
    elementToControl.querySelectorAll(":not(div):not(hi-tooltip)").forEach((el) => el.setAttribute("required", ""));
  }
  /**
   * getValueToLookfor(valueToLookFor, triggerType)
   * 
   * This function processes the `valueToLookFor` parameter based on the `triggerType` and returns a flat array of values
   * that will trigger the condition. It ensures the values are formatted consistently for comparison.
   * 
   * @param {string | Array} valueToLookFor - The value(s) to look for, which can be a string or an array.
   * @param {string} triggerType - The type of trigger that determines how the value should be processed.
   * 
   * @returns {Array} A flat array of processed values that will trigger the condition.
   */
  getValueToLookfor(valueToLookFor, triggerType) {
    let formattedReturnValue = valueToLookFor;
    switch (triggerType) {
      case "any":
      case "none": {
        formattedReturnValue = valueToLookFor[0].split(",").map((item) => item.trim());
        break;
      }
      default: {
        break;
      }
    }
    return this.flattenArrayAndLowerCaseValues(formattedReturnValue);
  }
  flattenArrayAndLowerCaseValues(valueToLookFor) {
    return valueToLookFor.flat().map((item) => item.toString().toLowerCase());
  }
  getTriggeredValue(triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return triggeredValue;
    return Array.isArray(triggeredValue) ? triggeredValue.map((item) => item.toString().toLowerCase()) : triggeredValue.toString().toLowerCase();
  }
  compareValues(valueToLookFor, triggeredValue, triggerType) {
    valueToLookFor = this.getValueToLookfor(valueToLookFor, triggerType);
    triggeredValue = this.getTriggeredValue(triggeredValue);
    const currentDate = /* @__PURE__ */ new Date((/* @__PURE__ */ new Date()).toISOString().split("T")[0] + "T00:00:00");
    switch (triggerType) {
      case "after":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]);
      case "any":
        return valueToLookFor.some((val) => triggeredValue.includes(val));
      case "before":
        return new Date(triggeredValue) < new Date(valueToLookFor[0]);
      case "between":
        return this.between(valueToLookFor, triggeredValue);
      case "contains":
        return valueToLookFor.some((val) => triggeredValue.includes(val));
      case "contains_all":
        return valueToLookFor.every((value) => triggeredValue.includes(value));
      case "contains_any":
        return this.containsAny(valueToLookFor, triggeredValue);
      case "current":
        return this.currentTimeframe(currentDate, valueToLookFor[0], /* @__PURE__ */ new Date(triggeredValue + "T00:00:00"));
      case "custom":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]) && new Date(triggeredValue) < new Date(valueToLookFor[1]);
      case "does_not_contain_all":
        return !valueToLookFor.every((val) => triggeredValue.includes(val));
      case "does_not_contain_any":
        return this.doesNotContainAny(valueToLookFor, triggeredValue);
      case "does_not_equal":
        return valueToLookFor[0] !== triggeredValue;
      case "ends_with":
        return triggeredValue.endsWith(valueToLookFor[0]) && triggeredValue !== "";
      case "exactly":
        return valueToLookFor[0] === triggeredValue;
      case "from":
        return this.from(valueToLookFor, triggeredValue);
      case "greater_than":
        return triggeredValue > valueToLookFor[0];
      case "greater_than_or_equal_to":
        return triggeredValue >= valueToLookFor[0];
      case "is":
        return valueToLookFor[0] === triggeredValue;
      case "is_empty":
        return triggeredValue === "" || triggeredValue === void 0 || triggeredValue?.length === 0;
      case "is_exactly":
        return valueToLookFor[0] === triggeredValue;
      case "is_not":
        return valueToLookFor[0] !== triggeredValue;
      case "is_not_empty":
        return this.isNotEmpty(triggeredValue);
      case "less_than":
        return triggeredValue < valueToLookFor[0];
      case "less_than_or_equal_to":
        return triggeredValue <= valueToLookFor[0];
      case "last":
        return this.dateInRange("last", valueToLookFor[0], triggeredValue, currentDate);
      case "next":
        return this.dateInRange("next", valueToLookFor[0], triggeredValue, currentDate);
      case "none":
        return !valueToLookFor.some((val) => triggeredValue.includes(val));
      case "since":
        return new Date(triggeredValue) > new Date(valueToLookFor[0]);
      case "starts_with":
        return triggeredValue.startsWith(valueToLookFor[0]) && triggeredValue !== "";
      case "to":
        return this.to(valueToLookFor, triggeredValue);
      default:
        throw new Error("Unsupported operator");
    }
  }
  isNotEmpty(triggeredValue) {
    if (Array.isArray(triggeredValue)) {
      return triggeredValue.length > 0;
    }
    return triggeredValue !== "" && triggeredValue !== void 0;
  }
  extractNumericPart(value) {
    const match = value.match(/^\d+/);
    return match ? parseInt(match[0], 10) : null;
  }
  startsWithNumber(value) {
    return /^\d/.test(value);
  }
  between(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return false;
    if (this.startsWithNumber(valueToLookFor[0]) && this.startsWithNumber(valueToLookFor[1])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookForStart = this.extractNumericPart(valueToLookFor[0]);
        const numericValueToLookForEnd = this.extractNumericPart(valueToLookFor[1]);
        if (numericTriggeredValue !== null && numericValueToLookForStart !== null && numericValueToLookForEnd !== null) {
          if (numericTriggeredValue > numericValueToLookForStart && numericTriggeredValue < numericValueToLookForEnd) return true;
          if (numericTriggeredValue === numericValueToLookForStart) return triggeredValue.localeCompare(valueToLookFor[0]) >= 0;
          if (numericTriggeredValue === numericValueToLookForEnd) return triggeredValue.localeCompare(valueToLookFor[1]) <= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return triggeredValue.localeCompare(valueToLookFor[0]) >= 0 && triggeredValue.localeCompare(valueToLookFor[1]) <= 0;
  }
  to(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return true;
    if (this.startsWithNumber(valueToLookFor[0])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookTo = this.extractNumericPart(valueToLookFor[0]);
        if (numericTriggeredValue !== null && numericValueToLookTo !== null) {
          if (numericTriggeredValue < numericValueToLookTo) return true;
          if (numericTriggeredValue === numericValueToLookTo) return triggeredValue.localeCompare(valueToLookFor[0]) <= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return triggeredValue.localeCompare(valueToLookFor[0]) <= 0;
  }
  from(valueToLookFor, triggeredValue) {
    if (this.isTriggeredValueEmpty(triggeredValue)) return false;
    if (this.startsWithNumber(valueToLookFor[0])) {
      if (this.startsWithNumber(triggeredValue)) {
        const numericTriggeredValue = this.extractNumericPart(triggeredValue);
        const numericValueToLookFrom = this.extractNumericPart(valueToLookFor[0]);
        if (numericTriggeredValue !== null && numericValueToLookFrom !== null) {
          if (numericTriggeredValue > numericValueToLookFrom) return true;
          if (numericTriggeredValue === numericValueToLookFrom) return triggeredValue.localeCompare(valueToLookFor[0]) >= 0;
          return false;
        } else {
          return false;
        }
      }
    }
    return valueToLookFor[0].localeCompare(triggeredValue) <= 0;
  }
  containsAny(valueToLookFor, triggeredValue) {
    if (Array.isArray(triggeredValue)) {
      if (!triggeredValue.length) return false;
      return triggeredValue.some((val) => valueToLookFor.includes(val));
    } else {
      if (this.isTriggeredValueEmpty(triggeredValue)) return false;
      return valueToLookFor.includes(triggeredValue);
    }
  }
  doesNotContainAny(valueToLookFor, triggeredValue) {
    if (!valueToLookFor.length) return true;
    if (this.isTriggeredValueEmpty(triggeredValue)) return true;
    return !valueToLookFor.some((val) => triggeredValue.includes(val));
  }
  isTriggeredValueEmpty(triggeredValue) {
    return [void 0, null, ""].includes(triggeredValue);
  }
  dateInRange(direction, rangeType, dateToCheck, currentDate) {
    const [year, month, day] = dateToCheck.split("-").map(Number);
    dateToCheck = new Date(Date.UTC(year, month - 1, day, 6, 0, 0));
    let rangeStart = new Date(currentDate);
    let rangeEnd = new Date(currentDate);
    let [_, number, unit] = rangeType.match(/(\d+)_((?:\w+_?)+)/);
    number = parseInt(number);
    switch (unit) {
      case "day":
      case "days":
        if (direction === "last") {
          rangeStart.setDate(currentDate.getDate() - number);
          rangeEnd.setDate(currentDate.getDate());
        } else {
          rangeStart.setDate(currentDate.getDate() + 1);
          rangeEnd.setDate(currentDate.getDate() + 1 + number);
        }
        break;
      case "calendar_week":
      case "calendar_weeks":
        if (direction === "last") {
          rangeStart.setDate(currentDate.getDate() - currentDate.getDay() - 7 * number);
          rangeEnd.setDate(currentDate.getDate() - currentDate.getDay());
        } else {
          rangeStart.setDate(currentDate.getDate() - currentDate.getDay() + 1);
          rangeEnd.setDate(currentDate.getDate() - currentDate.getDay() + 7 * number);
        }
        break;
      case "calendar_month":
      case "calendar_months":
        if (direction === "last") {
          rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - number, 1);
          rangeEnd.setMonth(currentDate.getMonth(), 0);
        } else {
          rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
          rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + number + 1, 0);
        }
        break;
      case "year":
        if (direction === "last") {
          rangeStart.setFullYear(currentDate.getFullYear() - number, 0, 1);
          rangeEnd.setFullYear(currentDate.getFullYear() - 1, 11, 31);
        } else {
          rangeStart.setFullYear(currentDate.getFullYear() + 1, 0, 1);
          rangeEnd.setFullYear(currentDate.getFullYear() + number, 11, 31);
        }
        break;
      default:
        throw new Error("Unsupported operator");
    }
    return dateToCheck >= rangeStart && dateToCheck <= rangeEnd;
  }
  currentTimeframe(currentDate, timeframe, date) {
    switch (timeframe) {
      case "current_day": {
        const startOfDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
        const endOfDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
        return date >= startOfDay && date < endOfDay;
      }
      case "current_week": {
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return date >= startOfWeek && date <= endOfWeek;
      }
      case "current_month": {
        const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        return date >= startOfMonth && date <= endOfMonth;
      }
      case "current_year": {
        const startOfYear = new Date(currentDate.getFullYear(), 0, 1);
        const endOfYear = new Date(currentDate.getFullYear() + 1, 0, 1);
        return date >= startOfYear && date < endOfYear;
      }
      default:
        return false;
    }
  }
}
